import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  DialogContentText,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { motion } from "framer-motion";
import { CustomButton, LoadingButton } from "./LoadingButton";
import {
  NoDataFoundRow,
  StyledTableCell,
  StyledTableRow,
} from "./StyledTableComponents";
import { formatDate2 } from "../../utils/helpers";

const AnimatedDialog = ({ open, onClose, children, ...props }) => {
  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason !== "backdropClick") {
          onClose();
        }
      }}
      PaperProps={{
        component: motion.div,
        initial: { opacity: 0, y: 50 },
        animate: { opacity: 1, y: 0 },
        exit: { opacity: 0, y: 50 },
        transition: { duration: 0.5 },
        ...props.PaperProps,
        sx: {
          borderRadius: "1rem",
          maxWidth: "500px",
          mx: "auto",
          // p:1,
          ...props.PaperProps?.sx,
        },
      }}
      {...props}
    >
      {children}
    </Dialog>
  );
};

export const LogoutConfirmationDialog = ({
  open,
  onClose,
  onConfirm,
  currentAction,
  handleClose,
  loading,
}) => {
  return (
    <AnimatedDialog open={open} onClose={onClose}>
      <DialogTitle sx={{ textAlign: "center", fontFamily: "Urbane-Demibold" }}>
        {currentAction === "delete"
          ? "Delete!"
          : currentAction === "activate"
          ? "Activate!"
          : "Logout!"}
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ fontFamily: "Urbane-light" }}>
          {currentAction === "activate"
            ? "Do you want to register or retrieve the old account?"
            : `Are you sure you want to ${
                currentAction === "delete" ? "Delete Account" : "Logout"
              }?`}
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "center" }}>
        <LoadingButton isLoading={loading} onClick={onConfirm} color="primary">
          Yes
        </LoadingButton>

        <CustomButton
          onClick={currentAction === "activate" ? handleClose : onClose}
          color="error"
        >
          No
        </CustomButton>
      </DialogActions>
    </AnimatedDialog>
  );
};
export const StatusConfirmationDialog = ({
  open,
  onClose,
  onConfirm,
  currentAction,
  handleClose,
  loading,
}) => {
  return (
    <AnimatedDialog open={open} onClose={onClose}>
      <DialogTitle sx={{ textAlign: "center", fontFamily: "Urbane-Demibold" }}>
        {currentAction === "timeout"
          ? "Session Timeout!"
          : currentAction === "Account is blocked."
          ? "Blocked!"
          : "Deleted!"}
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ fontFamily: "Urbane-light" }}>
          {currentAction === "timeout" &&
            "Your session has timed out. Please log in again."}
          {currentAction === "Account is blocked." && (
            <>
              Your account has been blocked by admin. Please contact{" "}
              <span
                // href="mailto:support@slate-acceleraator.com"
                style={{ color: "blue" }}
              >
                customersupport@slate-accelerator.com
              </span>
              .
            </>
          )}
          {currentAction !== "timeout" &&
            currentAction !== "Account is blocked." &&
            "Your account has been deleted."}
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "center" }}>
        <LoadingButton isLoading={loading} onClick={onConfirm} color="error">
          OK
        </LoadingButton>
      </DialogActions>
    </AnimatedDialog>
  );
};
export const ConfirmationDialog = ({
  open,
  onClose,
  onConfirm,
  loading,
  currentAction,
  candidateStatus,
}) => {
  return (
    <AnimatedDialog open={open} onClose={onClose}>
      <DialogTitle sx={{ textAlign: "center", fontFamily: "Urbane-Demibold" }}>
        {currentAction === "cancelSubscription"
          ? "Confirm Subscription Cancellation !"
          : currentAction === "updateCandidateStatus"
          ? "Confirm Candidate Status Update !"
          : "Confirm Stripe Account Change !"}
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ fontFamily: "Urbane-light" }}>
          {currentAction === "cancelSubscription"
            ? "Are you sure you want to cancel your subscription?"
            : currentAction === "updateCandidateStatus"
            ? candidateStatus === "active" ?
            ` Do you want to Activate this candidate?` :
            `Are you sure you want to De-Activate this candidate?`
            : "Are you sure you want to change your Stripe Account?"}
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "center" }}>
        <LoadingButton isLoading={loading} onClick={onConfirm} color="primary">
          Yes
        </LoadingButton>
        <LoadingButton onClick={onClose} color="error">
          No
        </LoadingButton>
      </DialogActions>
    </AnimatedDialog>
  );
};
export const CandidateConfirmationDialog = ({ open, onClose, onConfirm }) => {
  return (
    <AnimatedDialog open={open} onClose={onClose}>
      <DialogTitle sx={{ textAlign: "center", fontFamily: "Urbane-Demibold" }}>
        Delete Profile !
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ fontFamily: "Urbane-light" }}>
          Are you sure you want to delete your profile from Slate Accelerator?
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "center" }}>
        <CustomButton onClick={onConfirm} color="primary">
          Yes
        </CustomButton>
        <CustomButton onClick={onClose} color="error">
          No
        </CustomButton>
      </DialogActions>
    </AnimatedDialog>
  );
};
export const SubscriptionHistoryDialog = ({
  open,
  onClose,
  onConfirm,
  data,
}) => {
  return (
    <AnimatedDialog open={open} onClose={onClose}>
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography
          variant="h6"
          sx={{ textAlign: "center", fontFamily: "Urbane-Demibold" }}
        >
          Subscription History
        </Typography>
        <IconButton
          edge="end"
          color="error"
          onClick={onClose}
          aria-label="close"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <TableContainer sx={{ width: "100%", margin: "auto", mt: 1 }}>
          <Table>
            <TableHead>
              <TableRow>
                {["No.", "start date", "Status", "subscription name"].map(
                  (header, index) => (
                    <StyledTableCell key={index}>{header}</StyledTableCell>
                  )
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {data && data.length > 0 ? (
                <>
                  {data
                    .filter((row) => row.subscription_status !== "initiated")
                    .map((row, index) => {
                      return (
                        <TableRow
                          key={index}
                          sx={{
                            "&:nth-of-type(odd)": {
                              backgroundColor: "#f5f5f5",
                            },
                            fontSize: "12px",
                          }}
                        >
                          <StyledTableRow sx={{ textAlign: "center" }}>
                            {index + 1}.
                          </StyledTableRow>

                          <StyledTableRow>
                            {formatDate2(row.subscription_strartdate)}
                          </StyledTableRow>

                          <StyledTableRow sx={{ textTransform: "capitalize" }}>
                            {row.subscription_status}
                          </StyledTableRow>
                          <StyledTableRow align="center">
                            {" "}
                            {row.subscription_name ?? "-"}
                          </StyledTableRow>
                        </TableRow>
                      );
                    })}
                </>
              ) : (
                <>
                  <NoDataFoundRow
                    colSpan={9}
                    message="No Subscription History Found"
                  />
                </>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
    </AnimatedDialog>
  );
};

export const SubscriptionHistoryDialogForAdmin = ({
  open,
  onClose,
  onConfirm,
  data,
}) => {
  //   {
  //     "subscription_id": "sub_1QAmqIFqAhL0uTFDAyc77ciY",
  //     "plan_id": "price_1PxSS7FqAhL0uTFDftCcnDG3",
  //     "status": "trialing",
  //     "current_period_end": "01-15-2025"
  // }
  console.log(data, "././././././././././././");
  return (
    <AnimatedDialog open={open} onClose={onClose}>
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography
          variant="h6"
          sx={{ textAlign: "center", fontFamily: "Urbane-Demibold" }}
        >
          Subscription History
        </Typography>
        <IconButton
          edge="end"
          color="error"
          onClick={onClose}
          aria-label="close"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <TableContainer sx={{ width: "100%", margin: "auto", mt: 1 }}>
          <Table>
            <TableHead>
              <TableRow>
                {["No.", "start date", "Status", "subscription name"].map(
                  (header, index) => (
                    <StyledTableCell key={index}>{header}</StyledTableCell>
                  )
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {data && data.length > 0 ? (
                <>
                  {data
                    .filter((row) => row.status !== "initiated")
                    .map((row, index) => {
                      return (
                        <TableRow
                          key={index}
                          sx={{
                            "&:nth-of-type(odd)": {
                              backgroundColor: "#f5f5f5",
                            },
                            fontSize: "12px",
                          }}
                        >
                          <StyledTableRow sx={{ textAlign: "center" }}>
                            {index + 1}.
                          </StyledTableRow>

                          <StyledTableRow>
                            {row.subscription_strartdate}
                          </StyledTableRow>

                          <StyledTableRow sx={{ textTransform: "capitalize" }}>
                            {row.subscription_status}
                          </StyledTableRow>
                          <StyledTableRow align="center">
                            {" "}
                            {row.subscription_name ?? "-"}
                          </StyledTableRow>
                        </TableRow>
                      );
                    })}
                </>
              ) : (
                <>
                  <NoDataFoundRow
                    colSpan={9}
                    message="No Subscription History Found"
                  />
                </>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
    </AnimatedDialog>
  );
};
